import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { formatINR } from "../lib/format";

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-ink-950">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-ink-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function StatTile({
  label,
  value,
  sub,
  tone = "default",
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "default" | "good" | "warning" | "critical";
}) {
  const toneClass =
    tone === "good" ? "text-emerald-700"
    : tone === "warning" ? "text-amber-700"
    : tone === "critical" ? "text-red-700"
    : "text-ink-950";
  return (
    <div className="card-surface p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</div>
      <div className={`tnum mt-2 text-2xl font-semibold ${toneClass}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-ink-500">{sub}</div>}
    </div>
  );
}

export function Panel({
  title,
  subtitle,
  actions,
  children,
  className = "",
}: {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card-surface ${className}`}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-200/70 px-5 py-3.5">
          <div>
            {title && <h2 className="text-sm font-semibold text-ink-900">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-xs text-ink-500">{subtitle}</p>}
          </div>
          {actions}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

/**
 * A primary action that cannot be taken yet, with the reason attached.
 *
 * A disabled button on its own is a dead end: the app knows exactly what is
 * missing and says nothing, so the click just fails silently. This keeps the
 * button disabled — it genuinely cannot work — and prints the reason beside
 * it, where the hand already is.
 */
export function BlockedAction({
  blocked,
  reason,
  children,
}: {
  blocked: boolean;
  reason: string;
  children: ReactNode;
}) {
  if (!blocked) return <>{children}</>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-ink-500">{reason}</span>
      {children}
    </div>
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-ink-300 px-6 py-10 text-center">
      <p className="text-sm font-medium text-ink-700">{title}</p>
      {hint && <p className="mt-1 max-w-md text-sm text-ink-500">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ProgressBar({
  pct,
  color = "#2a78d6",
  achieved = false,
}: {
  pct: number;
  color?: string;
  achieved?: boolean;
}) {
  const width = Math.max(0, Math.min(100, pct));
  return (
    <div
      className="h-2 w-full overflow-hidden rounded-full bg-ink-200"
      role="progressbar"
      aria-valuenow={Math.round(width)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full rounded-full transition-[width] duration-500"
        style={{ width: `${width}%`, background: achieved ? "#067647" : color }}
      />
    </div>
  );
}

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "good" | "warning" | "critical" | "info" }) {
  const cls =
    tone === "good" ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
    : tone === "warning" ? "bg-amber-50 text-amber-800 ring-1 ring-amber-200"
    : tone === "critical" ? "bg-red-50 text-red-800 ring-1 ring-red-200"
    : tone === "info" ? "bg-accent-50 text-accent-800 ring-1 ring-accent-200"
    : "bg-ink-100 text-ink-700 ring-1 ring-ink-200";
  return <span className={`chip ${cls}`}>{children}</span>;
}

export function Money({ value, compact, className = "" }: { value: number; compact?: boolean; className?: string }) {
  return <span className={`tnum ${className}`}>{formatINR(value, { compact })}</span>;
}

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-sm text-ink-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-ink-300 border-t-accent-600" />
      {label}
    </div>
  );
}

export function ErrorBanner({ message, onDismiss }: { message: string; onDismiss?: () => void }) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
      <span>{message}</span>
      {onDismiss && (
        <button onClick={onDismiss} className="text-red-600 hover:text-red-800" aria-label="Dismiss">
          ✕
        </button>
      )}
    </div>
  );
}

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Actions pinned below the scrolling body, so Save stays reachable. */
  footer?: ReactNode;
  wide?: boolean;
}) {
  if (!open) return null;
  // Drawn on <body>, not where it is used: a modal opened from the sticky
  // menu would otherwise sit inside the menu's stacking context, behind the
  // page — and vanish with the menu on a phone.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/40 p-4 sm:p-8">
      <div
        className={`card-surface flex max-h-full w-full flex-col ${wide ? "max-w-3xl" : "max-w-xl"}`}
      >
        <header className="flex shrink-0 items-center justify-between border-b border-ink-200/70 px-5 py-3.5">
          <h2 className="text-sm font-semibold text-ink-900">{title}</h2>
          <button onClick={onClose} className="btn-ghost px-2 py-1" aria-label="Close">
            ✕
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto p-5">{children}</div>
        {footer && (
          <footer className="shrink-0 border-t border-ink-200/70 px-5 py-3.5">{footer}</footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
